/**
 * simQuota (DE-266) — payload-size estimation + SIM-quota bucketing for the
 * pre-deploy previews on the Releases page (Compose & deploy, and Deploy again).
 *
 * The estimate is deliberately an upper bound and labelled "up to" in the UI:
 * content is delta-downloaded device-side (a device that already holds most of
 * a profile fetches far less), while an OS image is always the full download.
 * INSUFFICIENT_MARGIN adds headroom for Mender/TLS overhead and concurrent
 * telemetry traffic — never present the number as exact.
 *
 * When a part's size can't be resolved that framing inverts, which is what
 * `confidence` tracks: a partial estimate is a LOWER bound, so "up to" would be
 * a lie and headroom can no longer be certified. The rule this module follows is
 * **gate on facts, warn on ignorance** — the same principle as the vendor-outage
 * path, which must never block a deploy either.
 */
import type {
  ContentProfile,
  DeviceSimStatus,
  ReleaseArtifact,
  ReleaseDeployParts,
  ReleaseLedgerParts,
} from '../api/hooks';

export const INSUFFICIENT_MARGIN = 1.2;

/**
 * How much of the payload we managed to size.
 *   exact   — every part sized; `bytes` is the upper bound the UI calls "up to".
 *   partial — some parts sized, some not; `bytes` is a LOWER bound, so headroom
 *             can't be certified (a shortfall against it is still a proof).
 *   none    — nothing sized, or no parts at all; `bytes` is 0 and means nothing.
 */
export type EstimateConfidence = 'exact' | 'partial' | 'none';

export interface ReleaseSizeEstimate {
  bytes: number;
  /** Parts whose size the registry doesn't know (null bytes, or a profile that
   *  has since been retired) — the estimate is a lower bound when non-empty. */
  unknownParts: string[];
  confidence: EstimateConfidence;
}

function bytesOf(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * A stored release's frozen ledger parts → the deploy-request shape the
 * estimator keys on. Mirrors the backend's own re-deploy resolution
 * (POST /releases/deploy): a part counts only when it carries the id the deploy
 * would actually resolve, so this sizes exactly what the re-deploy will send.
 * An empty result means the release row predates part tracking — the backend
 * answers 422 and there is nothing to estimate.
 */
export function deployPartsFromLedger(
  parts: ReleaseLedgerParts | null | undefined,
): ReleaseDeployParts {
  const p: ReleaseDeployParts = {};
  if (parts?.os?.artifactId) p.os = { artifactId: parts.os.artifactId };
  if (parts?.content?.profileId) p.content = { profileId: parts.content.profileId };
  if (parts?.app?.artifactId) p.app = { artifactId: parts.app.artifactId };
  if (parts?.ucfw?.artifactId) p.ucfw = { artifactId: parts.ucfw.artifactId };
  return p;
}

export function estimateReleaseBytes(
  parts: ReleaseDeployParts,
  osArtifacts: ReleaseArtifact[],
  appArtifacts: ReleaseArtifact[],
  fwArtifacts: ReleaseArtifact[],
  profiles: ContentProfile[],
): ReleaseSizeEstimate {
  let bytes = 0;
  let known = 0;
  const unknownParts: string[] = [];
  const add = (label: string, v: number | null) => {
    if (v === null) unknownParts.push(label);
    else { bytes += v; known += 1; }
  };
  if (parts.os) {
    add('OS image', bytesOf(osArtifacts.find((a) => a.id === parts.os!.artifactId)?.bytes));
  }
  if (parts.app) {
    add('app bundle', bytesOf(appArtifacts.find((a) => a.id === parts.app!.artifactId)?.bytes));
  }
  if (parts.ucfw) {
    add('uC firmware', bytesOf(fwArtifacts.find((a) => a.id === parts.ucfw!.artifactId)?.bytes));
  }
  if (parts.content) {
    add(
      'content profile',
      bytesOf(profiles.find((p) => p.id === parts.content!.profileId)?.total_bytes),
    );
  }
  // known === 0 folds "no parts at all" into `none`, which is exactly right: a
  // legacy release row with nothing re-deployable can't be judged either.
  const confidence: EstimateConfidence =
    known === 0 ? 'none' : unknownParts.length > 0 ? 'partial' : 'exact';
  return { bytes, unknownParts, confidence };
}

/**
 * Quota buckets for a target set. Ack-gating applies to blocked+insufficient
 * only; `tight` warns without gating; `unmanaged` is the mixed-carrier normal
 * case (Telia SIM, SIM outside our Onomondo org, no modem) and `unknown` is
 * "couldn't judge" — either the check failed, or the payload size is unknown so
 * there is nothing to compare the remaining data against.
 */
export interface QuotaBuckets {
  blocked: string[]; // cap already hit — data is off right now
  insufficient: string[]; // remaining < estimate
  tight: string[]; // remaining < estimate × margin
  ok: string[]; // headroom, or uncapped
  unmanaged: string[]; // not an Onomondo-managed SIM — nothing to check
  unknown: string[]; // check failed / integration unconfigured / size unknown
}

export function bucketQuota(
  things: string[],
  sims: Record<string, DeviceSimStatus> | undefined,
  estimate: ReleaseSizeEstimate,
): QuotaBuckets {
  const b: QuotaBuckets = {
    blocked: [],
    insufficient: [],
    tight: [],
    ok: [],
    unmanaged: [],
    unknown: [],
  };
  for (const thing of things) {
    const d = sims?.[thing];
    if (!d) {
      b.unknown.push(thing);
      continue;
    }
    if (d.error) {
      if (d.error === 'no-iccid' || d.error === 'not-onomondo' || d.error === 'sim-not-found') {
        b.unmanaged.push(thing);
      } else {
        b.unknown.push(thing);
      }
      continue;
    }
    const q = d.sim?.quota;
    if (!q) {
      b.ok.push(thing); // uncapped — usage-based billing only, nothing to exceed
      continue;
    }
    // `blocked` needs no size: a SIM at its cap passes no data at any payload.
    // It is therefore the one verdict that survives a worthless estimate, and
    // the reason the ack gate stays meaningful when confidence is 'none'.
    if (q.blocked) {
      b.blocked.push(thing);
      continue;
    }
    if (estimate.confidence === 'none') {
      // A capped SIM with data left and no payload size to compare it against
      // is genuinely unjudged — calling it OK would be the false reassurance
      // this bucketing exists to avoid.
      b.unknown.push(thing);
      continue;
    }
    // On a partial estimate `bytes` is a lower bound, so a shortfall against it
    // still PROVES the download can't complete — only the OK verdict loses its
    // authority, and describeEstimate() re-words it accordingly.
    if (q.remainingBytes < estimate.bytes) b.insufficient.push(thing);
    else if (q.remainingBytes < estimate.bytes * INSUFFICIENT_MARGIN) b.tight.push(thing);
    else b.ok.push(thing);
  }
  return b;
}

/**
 * Operator-facing copy derived from the estimate's confidence. Kept here rather
 * than inline in the preview so the wording is pure and testable (the same
 * pattern as the copy helpers in lib/releaseId.ts), and so the preview
 * component stays ignorant of which deploy surface mounted it.
 */
export interface EstimateCopy {
  /** How to qualify the byte figure; null → show no number at all. */
  qualifier: 'up to' | 'at least' | null;
  /** Non-null whenever confidence !== 'exact' — says what the gap means. */
  caveat: string | null;
  okLabel: string;
  okHint: string;
  unknownHint: string;
}

export function describeEstimate(e: ReleaseSizeEstimate): EstimateCopy {
  const missing = e.unknownParts.join(' + ');
  if (e.confidence === 'exact') {
    return {
      qualifier: 'up to',
      caveat: null,
      okLabel: 'Quota OK',
      okHint: 'enough headroom, or no data cap set',
      unknownHint: 'quota could not be checked right now',
    };
  }
  if (e.confidence === 'partial') {
    return {
      qualifier: 'at least',
      caveat: `The registry has no size for ${missing}, so the real download is larger`
        + ' — devices below are judged against the sized parts only.',
      okLabel: 'Enough for the sized parts',
      okHint: 'headroom for the parts we could size — the unsized parts are not counted',
      unknownHint: 'quota could not be checked right now',
    };
  }
  return {
    qualifier: null,
    caveat: missing
      ? `No size could be resolved for ${missing}, so quota headroom cannot be judged`
        + ' — only SIMs already at their cap are flagged.'
      : 'This release records no re-deployable parts, so there is nothing to size'
        + ' — only SIMs already at their cap are flagged.',
    okLabel: 'No data cap',
    okHint: 'uncapped SIM — nothing to exceed',
    unknownHint:
      'quota could not be checked, or the download size is unknown so headroom cannot be judged',
  };
}
